/**
 * Projects (CRM side) — the delivery side of a deal: customer, deal, manager, dates and milestones.
 * Partners see the same records read-only in their portal. Gated on the `opportunities` screen.
 */
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, Modal, Field, SearchInput, Drawer, Spinner } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { pushToast } from '../ui/uiSlice.js';
import { date } from '../../utils/formatters.js';
import * as api from '../../api/projects.api.js';
import * as oppApi from '../../api/opportunities.api.js';
import * as usersApi from '../../api/users.api.js';
import { ProjectStatusBadge, ProjectDetail, Progress, PROJECT_STATUS, MILESTONE_STATUS } from './projectShared.jsx';

const errText = (e) => e?.details?.[0]?.message ?? e?.message ?? 'Something went wrong';
const BLANK = { name: '', opportunity_id: '', project_manager_id: '', status: 'planning', start_date: '', expected_end_date: '', description: '' };

export default function ProjectsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 300);
  const [editing, setEditing] = useState(null); // null | 'new' | project
  const [openId, setOpenId] = useState(null);

  const list = useInfiniteQuery({
    queryKey: ['projects', { status, dq }], queryFn: ({ pageParam }) => api.listProjects({ status, q: dq, cursor: pageParam, limit: 25 }),
    initialPageParam: undefined, getNextPageParam: (l) => l.page?.next_cursor ?? undefined,
  });
  const rows = (list.data?.pages ?? []).flatMap((p) => p.data);
  const refresh = () => qc.invalidateQueries({ queryKey: ['projects'] });

  const columns = [
    { key: 'project_no', header: 'Project ID', render: (r) => <span className="fw-600">{r.project_no}</span> },
    { key: 'name', header: 'Project', render: (r) => (<div><div className="fw-600">{r.name}</div><div className="t-xs text-muted">{r.customer?.name ?? '—'}</div></div>) },
    { key: 'deal', header: 'Deal', render: (r) => r.deal?.name ?? '—' },
    { key: 'pm', header: 'Manager', render: (r) => r.project_manager?.name ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <ProjectStatusBadge status={r.status} /> },
    { key: 'dates', header: 'Timeline', render: (r) => <span className="t-xs">{date(r.start_date)} → {date(r.expected_end_date)}</span> },
    { key: 'progress', header: 'Progress', render: (r) => <Progress pct={r.progress_pct} done={r.milestones_done} total={r.milestones_total} /> },
  ];

  return (
    <>
      <PageHeader title="Projects" subtitle="Delivery projects and their milestones — partners see theirs read-only">
        {can('opportunities', 'create') && <button type="button" className="btn btn-add btn-sm-x" onClick={() => setEditing('new')}><i className="bi bi-plus-lg me-1" aria-hidden="true" />New project</button>}
      </PageHeader>
      <Card bare>
        <div className="d-flex flex-wrap gap-2 p-3 pb-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search project, ID, customer…" />
          <select className="form-select form-select-sm w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <DataTable columns={columns} rows={rows} isLoading={list.isLoading} error={list.error} onRetry={list.refetch} onRowClick={(r) => setOpenId(r.id)}
          emptyTitle="No projects yet" emptyMessage="Create a project from a won deal to track its milestones." />
        {list.hasNextPage && <div className="p-3 text-center"><button type="button" className="btn btn-edit btn-sm-x" onClick={() => list.fetchNextPage()}>Load more</button></div>}
      </Card>
      <ProjectDrawer id={openId} onClose={() => setOpenId(null)} onEdit={(p) => setEditing(p)} onChanged={refresh} canEdit={can('opportunities', 'edit')} />
      <ProjectModal value={editing} onClose={() => setEditing(null)} onDone={(p) => { setEditing(null); refresh(); qc.invalidateQueries({ queryKey: ['project', p.id] }); dispatch(pushToast({ tone: 'success', message: `${p.project_no} saved` })); }} />
    </>
  );
}

function ProjectDrawer({ id, onClose, onEdit, onChanged, canEdit }) {
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const detail = useQuery({ queryKey: ['project', id], queryFn: () => api.getProject(id), enabled: Boolean(id) });
  const [ms, setMs] = useState({ name: '', due_date: '' });
  const reload = () => { qc.invalidateQueries({ queryKey: ['project', id] }); onChanged(); };
  const fail = (e) => dispatch(pushToast({ tone: 'danger', message: 'Could not save', detail: errText(e) }));
  const add = useMutation({ mutationFn: () => api.addMilestone(id, { name: ms.name.trim(), due_date: ms.due_date || null }), onSuccess: () => { setMs({ name: '', due_date: '' }); reload(); }, onError: fail });
  const setStatus = useMutation({ mutationFn: ({ mid, status }) => api.updateMilestone(id, mid, { status }), onSuccess: reload, onError: fail });
  const remove = useMutation({ mutationFn: (mid) => api.deleteMilestone(id, mid), onSuccess: reload, onError: fail });
  const p = detail.data;
  return (
    <Drawer open={Boolean(id)} onClose={onClose} title={p?.name ?? 'Project'} subtitle={p?.project_no}>
      {detail.isLoading || !p ? <Spinner /> : (
        <div className="d-flex flex-column gap-3">
          {canEdit && <div><button type="button" className="btn btn-edit btn-sm-x" onClick={() => onEdit(p)}><i className="bi bi-pencil me-1" aria-hidden="true" />Edit project</button></div>}
          <ProjectDetail
            project={p}
            renderMilestoneActions={canEdit ? (m) => (
              <span className="d-flex gap-1 align-items-center">
                <select className="form-select form-select-sm w-auto" value={m.status} disabled={setStatus.isPending} onChange={(e) => setStatus.mutate({ mid: m.id, status: e.target.value })}>
                  {Object.entries(MILESTONE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Delete milestone" disabled={remove.isPending} onClick={() => remove.mutate(m.id)}><i className="bi bi-trash" aria-hidden="true" /></button>
              </span>
            ) : undefined}
          />
          {canEdit && (
            <form className="d-flex flex-wrap gap-2 align-items-end" onSubmit={(e) => { e.preventDefault(); if (ms.name.trim()) add.mutate(); }}>
              <div className="flex-grow-1"><Field label="Add milestone"><input className="form-control" value={ms.name} onChange={(e) => setMs({ ...ms, name: e.target.value })} placeholder="e.g. User acceptance testing" /></Field></div>
              <div><Field label="Due"><input type="date" className="form-control" value={ms.due_date} onChange={(e) => setMs({ ...ms, due_date: e.target.value })} /></Field></div>
              <button type="submit" className="btn btn-add btn-sm-x mb-1" disabled={add.isPending || !ms.name.trim()}>{add.isPending ? <ButtonSpinner /> : 'Add'}</button>
            </form>
          )}
        </div>
      )}
    </Drawer>
  );
}

function ProjectModal({ value, onClose, onDone }) {
  const isNew = value === 'new';
  const open = Boolean(value);
  const [f, setF] = useState(BLANK);
  const [err, setErr] = useState('');
  const [seen, setSeen] = useState(null);
  if (open && seen !== (isNew ? 'new' : value.id)) {
    setSeen(isNew ? 'new' : value.id); setErr('');
    setF(isNew ? BLANK : { name: value.name, opportunity_id: value.deal?.id ?? '', project_manager_id: value.project_manager?.id ?? '', status: value.status, start_date: value.start_date ?? '', expected_end_date: value.expected_end_date ?? '', description: value.description ?? '' });
  }
  if (!open && seen) setSeen(null);
  const deals = useQuery({ queryKey: ['projects', 'deal-options'], queryFn: () => oppApi.listOpportunities({ limit: 100 }), enabled: open, staleTime: 60_000 });
  const users = useQuery({ queryKey: ['projects', 'user-options'], queryFn: () => usersApi.listUsers({ limit: 200 }), enabled: open, staleTime: 300_000 });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name.trim(), opportunity_id: f.opportunity_id || null, project_manager_id: f.project_manager_id || null, status: f.status, start_date: f.start_date || null, expected_end_date: f.expected_end_date || null, description: f.description.trim() || null };
      return isNew ? api.createProject(body) : api.updateProject(value.id, body, value.row_version);
    },
    onSuccess: onDone, onError: (e) => setErr(errText(e)),
  });
  if (!open) return null;
  const submit = (e) => { e.preventDefault(); if (!f.name.trim()) { setErr('A project name is required.'); return; } setErr(''); save.mutate(); };
  return (
    <Modal open onClose={onClose} title={isNew ? 'New project' : `Edit ${value.project_no}`} size="lg"
      footer={(<><button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button><button type="submit" form="projectForm" className="btn btn-save btn-sm-x" disabled={save.isPending}>{save.isPending ? <><ButtonSpinner />Saving…</> : 'Save project'}</button></>)}>
      <form id="projectForm" onSubmit={submit} noValidate>
        {err && <div className="alert alert-danger py-2 t-sm">{err}</div>}
        <div className="row g-3">
          <div className="col-12"><Field label="Project name" required><input className="form-control" value={f.name} onChange={set('name')} autoFocus /></Field></div>
          <div className="col-md-6"><Field label="Related deal" help="The customer comes from the deal. Partners see the project when the deal came from their lead.">
            <select className="form-select" value={f.opportunity_id} onChange={set('opportunity_id')}>
              <option value="">{deals.isLoading ? 'Loading…' : 'No deal'}</option>
              {(deals.data?.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></Field></div>
          <div className="col-md-6"><Field label="Project manager">
            <select className="form-select" value={f.project_manager_id} onChange={set('project_manager_id')}>
              <option value="">{users.isLoading ? 'Loading…' : 'Unassigned'}</option>
              {(users.data?.data ?? []).filter((u) => u.status === 'active' && !u.partner_id).map((u) => <option key={u.id} value={u.id}>{u.full_name ?? u.email}</option>)}
            </select></Field></div>
          <div className="col-md-4"><Field label="Status"><select className="form-select" value={f.status} onChange={set('status')}>{Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></Field></div>
          <div className="col-md-4"><Field label="Start date"><input type="date" className="form-control" value={f.start_date} onChange={set('start_date')} /></Field></div>
          <div className="col-md-4"><Field label="Expected completion"><input type="date" className="form-control" value={f.expected_end_date} onChange={set('expected_end_date')} /></Field></div>
          <div className="col-12"><Field label="Description"><textarea className="form-control" rows={3} value={f.description} onChange={set('description')} /></Field></div>
        </div>
      </form>
    </Modal>
  );
}
